import { MockServiceStore } from "./MockServiceStore";

/**
 * StructureCast - Structure (a precast concrete structure assembly)
 */
export interface StructureCastStructure {
  id: string;
  projectId: string;
  markNumber: string;
  stationReference: string;
  overallLength: number;
  overallWidth: number;
  overallHeight: number;
  totalWeight: number;
  totalCubicYards: number;
  status: "planned" | "in_progress" | "cast_complete" | "shipped";
  notes: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Heritage Ranch WRRF — Sanitary Sewer (Job #25-1585)
 * Mid-State Concrete Products, LLC — Santa Maria, CA — 48" sanitary sewer manhole network.
 *
 * Each structure is a 48" inside-diameter sanitary sewer manhole assembled from:
 *   - 1 base (MH48B##H — 48" Ext Base w/ integral shaft, invert & Press-Seal pipe boots)
 *   - 0-2 risers/shafts (MH48S12/24/36/48 — 48"d x 12/24/36/48" tall MH riser)
 *   - 1 eccentric cone (MH48CE18/24/36 — 48"d x 24"d x 18/24/36" tall)
 *   - 1-2 grade rings (MHGR2402 or MHGR2403 — 24" dia)
 *   - 1 frame & cover (1900 Sanitary Sewer CPH frame + cover, HW190023) — accessory, not concrete
 *
 * Outside diameter 60" (48"id + 6" walls). Dimensions in inches. Weight in lbs. Volume in CY.
 */

interface StructureDef {
  markNumber: string;
  cy: number;
  weightLbs: number;
  station: string;
  heightIn: number;     // overall precast height
}

const structureDefs: StructureDef[] = [
  // Group 1: Deep Manholes (cast_complete)
  { markNumber: "SSMH-01", cy: 3.01, weightLbs: 12055, station: "Sta 1+00 — WRRF Influent, 48\" Deep MH (8.5')", heightIn: 102 },
  { markNumber: "SSMH-02", cy: 2.82, weightLbs: 11265, station: "Sta 3+25 — Nacimiento Lake Dr, 48\" Deep MH (7.5')", heightIn: 90 },
  // Group 2: Standard Depth Manholes (in_progress)
  { markNumber: "SSMH-03", cy: 2.62, weightLbs: 10480, station: "Sta 5+50 — Nacimiento Lake Dr, 48\" Std MH (6.5')", heightIn: 78 },
  { markNumber: "SSMH-04", cy: 2.62, weightLbs: 10480, station: "Sta 7+75 — Heritage Loop Rd, 48\" Std MH (6.5')", heightIn: 78 },
  { markNumber: "SSMH-05", cy: 2.33, weightLbs: 9295, station: "Sta 10+00 — Heritage Loop Rd, 48\" Std MH (5.5')", heightIn: 66 },
  // Group 3: Mid-Depth Manholes (planned)
  { markNumber: "SSMH-06", cy: 2.33, weightLbs: 9295, station: "Sta 12+25 — Cabrillo Way, 48\" Mid MH (5.25')", heightIn: 63 },
  { markNumber: "SSMH-07", cy: 2.12, weightLbs: 8470, station: "Sta 14+50 — Lakeview Dr, 48\" Mid MH (4.5')", heightIn: 54 },
  // Group 4: Shallow Manholes (planned)
  { markNumber: "SSMH-08", cy: 2.12, weightLbs: 8470, station: "Sta 16+75 — Lakeview Dr, 48\" Shallow MH (4.5')", heightIn: 54 },
  { markNumber: "SSMH-09", cy: 2.12, weightLbs: 8470, station: "Sta 19+00 — Pinion Trail, 48\" Shallow MH (4.5')", heightIn: 54 },
  // Unscheduled — added late to scope (WRRF yard piping add)
  { markNumber: "SSMH-10", cy: 1.97, weightLbs: 7865, station: "Sta 21+25 — Pinion Trail, 48\" Shallow MH (4.5') — ADD", heightIn: 54 },
];

const generateStructures = (): StructureCastStructure[] => {
  return structureDefs.map((def, idx) => {
    const num = String(idx + 1).padStart(2, "0");

    // Status distribution: first 2 cast_complete, next 3 in_progress, rest planned
    let status: StructureCastStructure["status"] = "planned";
    if (idx < 2) status = "cast_complete";
    else if (idx < 5) status = "in_progress";

    return {
      id: `sc-struct-${num}`,
      projectId: "sc-proj-1",
      markNumber: def.markNumber,
      stationReference: def.station,
      overallLength: 60,
      overallWidth: 60,
      overallHeight: def.heightIn,
      totalWeight: def.weightLbs,
      totalCubicYards: def.cy,
      status,
      notes: "48\" Sanitary Sewer Manhole",
      createdAt: "2025-05-29T00:00:00.000Z",
      updatedAt: "2026-01-19T00:00:00.000Z",
    };
  });
};

export const mockStructureCastStructures: StructureCastStructure[] = generateStructures();

export const createMockStructureCastStructureStore = () =>
  new MockServiceStore<StructureCastStructure>({
    mockData: mockStructureCastStructures,
    idField: "id",
    simulateDelay: 200,
    persistKey: "preferred-structurecast-structures",
  });

export default mockStructureCastStructures;
